/**
 * Gantt Row Component
 *
 * Displays a single theme row with its items as timeline bars.
 * Items are efficiently packed into lanes to minimize vertical space.
 */

import { useMemo } from 'react';

import { GanttItem } from './GanttItem';

import type { RoadmapThemeWithItems, RoadmapItemWithInvestment } from '@medsuite/models';

type ZoomLevel = 'year' | 'quarter' | 'month';

// Width constants (must match GanttChart)
const MONTH_COLUMN_WIDTH = 140;
const THEME_COLUMN_WIDTH = 192;

interface GanttRowProps {
  theme: RoadmapThemeWithItems;
  items: RoadmapItemWithInvestment[];
  yearStart: Date;
  yearEnd: Date;
  columnCount: number; // Number of columns (4 for quarters, 36 for months)
  zoomLevel?: ZoomLevel;
  onItemClick: (item: RoadmapItemWithInvestment) => void;
}

// Helper to check if two date ranges overlap
const rangesOverlap = (start1: Date, end1: Date, start2: Date, end2: Date): boolean => {
  return start1 <= end2 && start2 <= end1;
};

/**
 * Assigns items to lanes (horizontal rows) to minimize vertical space.
 * Items that don't overlap in time can share the same lane.
 * Uses a greedy algorithm: sort by start date, assign each item to the first available lane.
 */
const assignItemsToLanes = (
  items: RoadmapItemWithInvestment[],
  yearStart: Date,
  yearEnd: Date
): Map<number, number> => {
  const laneAssignments = new Map<number, number>(); // itemId -> laneIndex

  if (items.length === 0) {
    return laneAssignments;
  }

  // Sort items by start date (earlier items get priority for lower lanes)
  const sortedItems = [...items].sort((a, b) => {
    const aStart = new Date(a.start_date);
    const bStart = new Date(b.start_date);
    return aStart.getTime() - bStart.getTime();
  });

  // Track which items are in each lane (by their visible date ranges)
  const lanes: Array<Array<{ start: Date; end: Date }>> = [];

  for (const item of sortedItems) {
    const itemStart = new Date(item.start_date);
    const itemEnd = new Date(item.end_date);

    // Clamp to visible range for overlap detection
    const visibleStart = itemStart < yearStart ? yearStart : itemStart;
    const visibleEnd = itemEnd > yearEnd ? yearEnd : itemEnd;

    // Find the first lane where this item doesn't overlap with any existing item
    let assignedLane = -1;
    for (let laneIndex = 0; laneIndex < lanes.length; laneIndex++) {
      const laneItems = lanes[laneIndex];
      const hasOverlap = laneItems.some((existing) =>
        rangesOverlap(visibleStart, visibleEnd, existing.start, existing.end)
      );

      if (!hasOverlap) {
        assignedLane = laneIndex;
        break;
      }
    }

    // If no existing lane works, create a new one
    if (assignedLane === -1) {
      assignedLane = lanes.length;
      lanes.push([]);
    }

    // Add this item to the assigned lane
    lanes[assignedLane].push({ start: visibleStart, end: visibleEnd });
    laneAssignments.set(item.roadmap_item_id, assignedLane);
  }

  return laneAssignments;
};

/**
 * GanttRow Component
 *
 * Features:
 * - Theme title on the left (sticky in month view for horizontal scrolling)
 * - Timeline grid with items
 * - Dynamic grid lines based on column count
 * - Efficient item packing (non-overlapping items share lanes)
 */
export function GanttRow({
  theme,
  items,
  yearStart,
  yearEnd,
  columnCount,
  zoomLevel = 'quarter',
  onItemClick,
}: GanttRowProps) {
  // Calculate the total days in the range
  const totalDays =
    Math.ceil((yearEnd.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  // Filter items that overlap with the visible range
  const visibleItems = useMemo(() => {
    return items.filter((item) => {
      const itemStart = new Date(item.start_date);
      const itemEnd = new Date(item.end_date);
      return itemEnd >= yearStart && itemStart <= yearEnd;
    });
  }, [items, yearStart, yearEnd]);

  // Assign items to lanes for efficient packing
  const laneAssignments = useMemo(() => {
    return assignItemsToLanes(visibleItems, yearStart, yearEnd);
  }, [visibleItems, yearStart, yearEnd]);

  // Calculate the number of lanes needed
  const laneCount = useMemo(() => {
    if (laneAssignments.size === 0) {
      return 0;
    }
    return Math.max(...Array.from(laneAssignments.values())) + 1;
  }, [laneAssignments]);

  // Calculate row height based on number of lanes (not items)
  // Each lane is 28px tall (h-7) with 4px spacing, plus 8px total padding (py-2)
  const itemHeight = 32; // 28px item + 4px gap
  const basePadding = 8; // 4px top + 4px bottom
  const minRowHeight = 60; // Minimum height for empty rows
  const calculatedHeight = basePadding + laneCount * itemHeight;
  const rowHeight = Math.max(minRowHeight, calculatedHeight);

  // Calculate position and width for each item
  const calculateItemPosition = (item: RoadmapItemWithInvestment) => {
    const itemStart = new Date(item.start_date);
    const itemEnd = new Date(item.end_date);

    // Clamp to visible range boundaries
    const visibleStart = itemStart < yearStart ? yearStart : itemStart;
    const visibleEnd = itemEnd > yearEnd ? yearEnd : itemEnd;

    // Calculate start offset (percentage)
    const startOffset = Math.ceil(
      (visibleStart.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)
    );
    const left = (startOffset / totalDays) * 100;

    // Calculate width (percentage)
    const duration =
      Math.ceil((visibleEnd.getTime() - visibleStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const width = (duration / totalDays) * 100;

    // Indicators for items extending beyond visible range
    const extendsBefore = itemStart < yearStart;
    const extendsAfter = itemEnd > yearEnd;

    return { left, width, extendsBefore, extendsAfter };
  };

  return (
    <div
      className="flex border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors"
      style={{ minHeight: `${rowHeight}px` }}
    >
      {/* Theme Title Column - sticky in month view for horizontal scrolling */}
      <div
        className={`px-4 py-3 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 ${
          zoomLevel === 'month' ? 'sticky left-0 z-10' : ''
        }`}
        style={{ width: THEME_COLUMN_WIDTH, minWidth: THEME_COLUMN_WIDTH }}
      >
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {theme.theme_title}
        </div>
        {theme.theme_context && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {theme.theme_context}
          </div>
        )}
      </div>

      {/* Timeline Area */}
      <div className="flex-1 relative">
        {/* Column Grid Lines */}
        <div className="absolute inset-0 flex pointer-events-none">
          {Array.from({ length: columnCount }).map((_, idx) => (
            <div
              key={idx}
              className={`${idx < columnCount - 1 ? 'border-r border-gray-200 dark:border-gray-700' : ''}`}
              style={
                zoomLevel === 'month'
                  ? { width: MONTH_COLUMN_WIDTH, minWidth: MONTH_COLUMN_WIDTH }
                  : { flex: 1 }
              }
            />
          ))}
        </div>

        {/* Items */}
        <div className="relative h-full py-2 px-1">
          {visibleItems.length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-600">
              No items
            </div>
          ) : (
            visibleItems.map((item) => {
              const position = calculateItemPosition(item);
              const laneIndex = laneAssignments.get(item.roadmap_item_id) ?? 0;
              return (
                <GanttItem
                  key={item.roadmap_item_id}
                  item={item}
                  left={position.left}
                  width={position.width}
                  extendsBefore={position.extendsBefore}
                  extendsAfter={position.extendsAfter}
                  onClick={() => onItemClick(item)}
                  stackIndex={laneIndex}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default GanttRow;
